import {
  customerTypeEnum,
  opportunities,
  opportunityStageHistory,
  propertyTypeEnum,
} from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import { emailSchema, phoneNumberSchema } from './common'
import { customerPropertySchema } from './customer'
import { selectOpportunityTagWithDetailsSchema } from './tag'

// Auto-generate base schemas from database table. internal* fields are
// stripped for non-staff roles, so make them optional on the response schema.
export const selectOpportunitySchema = createSelectSchema(opportunities, {
  internalAcquisitionData: z.unknown().optional(),
})

// Schema for JSONB data field structure
export const opportunityDataSchema = z.object({
  address: z
    .object({
      street: z.string().optional(),
      city: z.string().optional(),
      postalCode: z.string().optional(),
      area: z.string().optional(),
      approximateLocation: z.string().optional(),
    })
    .optional(),
  // Property information - uses same structure as property entity
  propertyType: z.enum(propertyTypeEnum.enumValues).optional(),
  constructionYear: z.number().optional(),
  floorArea: z.string().or(z.number()).optional(),
  numberOfFloors: z.number().optional(),
  lotArea: z.number().optional(),
  property: z
    .object({
      type: z.enum(propertyTypeEnum.enumValues).optional(),
      estimatedSize: z.number().optional(),
      estimatedYear: z.number().optional(),
      hasGarage: z.boolean().optional(),
      interestedServices: z.array(z.string()).optional(),
    })
    .optional(),
  preferences: z
    .object({
      contactMethod: z.enum(['phone', 'email', 'sms']).optional(),
      bestCallTime: z.string().optional(),
      language: z.enum(['fi', 'sv', 'en']).optional(),
    })
    .optional(),
  // Loss information (set when an opportunity is marked as lost)
  lossReason: z.string().optional(),
  lossNotes: z.string().optional(),
  followUpTaskId: z.number().optional(),
})

// Stage history schemas
export const selectOpportunityStageHistorySchema = createSelectSchema(
  opportunityStageHistory,
)
export const insertOpportunityStageHistorySchema = createInsertSchema(
  opportunityStageHistory,
).omit({
  id: true,
  createdAt: true,
})

// Schema for stage history with relations
export const selectOpportunityStageHistoryWithRelationsSchema =
  selectOpportunityStageHistorySchema.extend({
    createdBy: z
      .object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      })
      .nullable()
      .optional(),
  })

// Schema for opportunity with relations
export const selectOpportunityWithRelationsSchema =
  selectOpportunitySchema.extend({
    data: opportunityDataSchema.nullable(),
    user: z
      .object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string().nullable().optional(),
        phoneNumber: phoneNumberSchema,
      })
      .nullable()
      .optional(),
    assignedTo: z
      .object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string().nullable(),
      })
      .nullable()
      .optional(),
    reportedBy: z
      .object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string().nullable(),
      })
      .nullable()
      .optional(),
    customer: z
      .object({
        id: z.number(),
        name: z.string(),
        type: z.enum(customerTypeEnum.enumValues),
        properties: z.array(customerPropertySchema).optional(),
      })
      .nullable()
      .optional(),
    stageHistory: z
      .array(selectOpportunityStageHistoryWithRelationsSchema)
      .optional(),
    opportunityTags: z.array(selectOpportunityTagWithDetailsSchema).optional(),
    // Latest-first; used to surface the most recent offer's title on lead cards.
    offers: z
      .array(
        z.object({
          id: z.number(),
          serviceDescription: z.string(),
        }),
      )
      .optional(),
  })

// Schema for creating an opportunity with user
export const upsertOpportunityWithRelationsSchema = createInsertSchema(
  opportunities,
)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    deletedAt: true,
  })
  .partial()
  .extend({
    // Allow updating specific user fields
    user: z
      .object({
        // Must be provided for id-reference to user record
        id: z.number().optional(),
        firstName: z.string().optional(),
        lastName: z.string().optional(),
        // '' (cleared form field) → undefined so the email-or-phone refine
        // below can pass on phone alone.
        email: z
          .union([emailSchema, z.literal('')])
          .optional()
          .transform((val) => val || undefined),
        phoneNumber: phoneNumberSchema.optional(),
      })
      .refine(
        (data) => {
          // User must have at least email OR phoneNumber
          return data.email || data.phoneNumber
        },
        {
          message: 'Käyttäjällä on oltava joko sähköposti tai puhelinnumero',
          path: ['email'], // Show error on email field
        },
      )
      .optional(),
    // Tags array for upsert operations - contains tag IDs
    tags: z.array(z.object({ tagId: z.number() })).optional(),
  })

// Type exports
export type Opportunity = z.infer<typeof selectOpportunitySchema>
export type OpportunityWithRelations = z.infer<
  typeof selectOpportunityWithRelationsSchema
>
export type OpportunityData = z.infer<typeof opportunityDataSchema>
export type UpsertOpportunityWithRelations = z.infer<
  typeof upsertOpportunityWithRelationsSchema
>
export type OpportunityStageHistory = z.infer<
  typeof selectOpportunityStageHistorySchema
>
export type OpportunityStageHistoryWithRelations = z.infer<
  typeof selectOpportunityStageHistoryWithRelationsSchema
>
export type InsertOpportunityStageHistory = z.infer<
  typeof insertOpportunityStageHistorySchema
>

// Active stages - opportunities in these stages are in the sales pipeline
export const ACTIVE_OPPORTUNITY_STAGES: readonly Opportunity['stage'][] = [
  'new',
  'contacted',
  'survey_visit',
  'offer',
] as const

// Stage labels - single source of truth
export const OPPORTUNITY_STAGE_LABELS = {
  new: 'Uusi liidi',
  contacted: 'Kontaktoitu',
  offer: 'Tarjous',
  won: 'Voitettu',
  lost: 'Menetetty',
  survey_visit: 'Kartoituskäynti',
  disqualified: 'Hylätty',
} as const

export const OPPORTUNITY_STAGE_COLORS: Record<Opportunity['stage'], string> = {
  new: 'blue',
  contacted: 'cyan',
  survey_visit: 'grape',
  offer: 'yellow',
  won: 'green',
  lost: 'red',
  disqualified: 'gray',
}

// Sale type labels
export const OPPORTUNITY_SALE_TYPE_LABELS = {
  subscription: 'Kuukausisopimus',
  single_time: 'Kertaosto',
} as const

export type OpportunitySaleType = keyof typeof OPPORTUNITY_SALE_TYPE_LABELS
